import { useCallback, useMemo } from "react";
import { useAppSelector } from "@/stores/hook";
import { selectCanEditTenantLeadConfig } from "@/rbac/rbacSlice";
import {
  useLeadViewDisplay,
  useSetLeadViewFieldOrder,
} from "@/features/leads/api/useLeadFormConfig";

/**
 * Lead-drawer sections whose field ORDER a Super Admin can arrange by dragging
 * the inputs in the drawer's edit mode (owner, 2026-10-15: "each input should
 * be click-drag to change its position — per tenant, only the super admin
 * gets that privilege"). Mirrors the server's LEAD_VIEW_FIELD_ORDER_SECTIONS.
 *
 *   basic    — Basic details: name, email, mobile, whatsapp, gender, location,
 *              branch, createdAt
 *   leadPref — Preference → Lead preference custom fields (customInputId)
 *   dealPref — Preference → Child/Deal preference custom fields (customInputId)
 *   source   — Lead Source & Assignment: source, campaign, adset, ad,
 *              reenquired, lastUpdated, dealCreated, leadCreated, assignedTo
 */
export type LeadViewFieldOrderSection =
  | "basic"
  | "leadPref"
  | "dealPref"
  | "source"
  /** The Overview's BLOCKS (priority, basic, preference, modules,
   *  dealAttachments, stageStatus, leadSource, notes) — the "Attach …" slot
   *  is placed like any field (owner, 2026-10-16). */
  | "overview";

/**
 * TENANT-WIDE order of one drawer section's fields, read off the shared
 * `/form-config` GET (`leadViewDisplay.fieldOrder.<section>`), applied by
 * BOTH the view and the edit mode of that section so a drag in edit mode is
 * what everyone sees afterwards. Keys the order has never seen (a custom
 * field created after the last drag, a field one mode has and the other
 * doesn't) render AFTER the ordered ones, in their natural order — so a new
 * field always shows up without anyone re-arranging. Empty order = natural.
 *
 * `canReorder` matches the server's assertSuperAdmin gate exactly, so a grip
 * that renders always implies the save will succeed.
 */
export function useLeadViewFieldOrder(section: LeadViewFieldOrderSection) {
  const display = useLeadViewDisplay();
  const canReorder = useAppSelector(selectCanEditTenantLeadConfig);
  const setOrder = useSetLeadViewFieldOrder();

  const raw = display?.fieldOrder?.[section];
  const orderKey = Array.isArray(raw) ? raw.map(String).join("|") : "";
  const order = useMemo<string[]>(
    () => (orderKey ? orderKey.split("|") : []),
    [orderKey],
  );

  const sortByOrder = useCallback(
    <T,>(items: T[], keyOf: (item: T) => string): T[] => {
      if (!order.length || items.length < 2) return items;
      const rank = new Map(order.map((k, i) => [k, i]));
      return items
        .map((item, i) => ({
          item,
          i,
          rank: rank.get(keyOf(item)) ?? Number.MAX_SAFE_INTEGER,
        }))
        .sort((a, b) => a.rank - b.rank || a.i - b.i)
        .map((x) => x.item);
    },
    [order],
  );

  const { mutate } = setOrder;
  /** Persist tenant-wide (optimistic on the shared config cache). */
  const save = useCallback(
    (next: string[]) => mutate({ section, order: next }),
    [section, mutate],
  );

  return {
    order,
    sortByOrder,
    canReorder,
    save,
    isSaving: setOrder.isPending,
  };
}
